import SEO from "../components/SEO";
import "../styles/PrivacyPolicy.css";

function PrivacyPolicy() {
  return (
    <div className="privacy-policy-container">
      <SEO title="Privacy Policy" description="MacroMatrix privacy policy — how we collect, use, and protect your data across our apps and services." path="/privacypolicy" />
      <h1>Privacy Policy – MacroMatrix Pvt Limited</h1>
      <p>
        <strong>Last Updated: March 04, 2026</strong>
      </p>
      <p>
        Welcome to MacroMatrix Pvt Limited and thanks for playing our Apps! We
        respect your privacy rights and recognize the importance of protecting
        the information collected about you. This Privacy Policy is designed to
        provide clarity about the information we collect, how we use it to
        provide a better gaming experience, as well as how you can exercise your
        privacy rights. This Privacy Policy applies whenever you play our Apps
        or otherwise access any of our other products, services, content,
        websites, and/or the other domains provided by us, together referred to
        as “Services.”
      </p>
      <p>
        In this policy, “MacroMatrix Pvt Limited” refers to MacroMatrix Pvt
        Limited and its affiliates, which shall mean subsidiaries, parent
        companies, joint ventures, and other corporate entities under common
        ownership. We may also refer to MacroMatrix Pvt Limited as “we,” “our,”
        or “us.” If you have any questions regarding our privacy policy or data
        protection practices, please contact us as described in Section 8.
      </p>
      <p>
        By using the Services, you agree to the collection, disclosure, storage,
        and use of your information as outlined in this Privacy Policy. If you
        do not want us to collect, store, use, or share your information in the
        ways described, you should not play our Apps or use our Services. We may
        amend this Privacy Policy from time to time, and we encourage you to
        review it regularly for changes.
      </p>
      <h2>1. Introduction</h2>
      <p>
        This policy explains how MacroMatrix Pvt Limited (“we,” “us,” or “our”)
        collects, uses, and protects information when you use our mobile
        applications and Apps. It applies to all apps and Apps published by
        MacroMatrix Pvt Limited. By using our services, you agree to the
        practices described in this Privacy Policy.
      </p>
      <p>
        We are committed to safeguarding your privacy and complying with all
        applicable laws and regulations, including the European GDPR (General
        Data Protection Regulation) and the California CCPA (California Consumer
        Privacy Act). We also adhere to privacy requirements of the Google Play
        Store and Apple App Store. If you have any questions or concerns about
        this policy, please contact us using the details above.
      </p>
      <h2>2. Data Collection</h2>
      <p>
        We collect various types of information to operate and improve our Apps,
        provide customer support, serve advertisements, and process
        transactions. The data we collect includes:
      </p>
      <ul>
        <li>
          <strong>Personal Information</strong> Information you provide to us
          directly. For example, when contacting support or creating an account
          (if applicable), you may provide your name, email address, phone
          number, or other contact details. We use this info to respond to you
          and facilitate any account-related features.
        </li>
        <li>
          <strong>Device and Technical Data</strong> Information collected
          automatically from your device when you use our apps. This may include
          your device type and model, operating system version, unique device
          identifiers (such as Advertising ID or IDFA), language, region,
          network provider, IP address, and other technical details. We collect
          this data to ensure compatibility, optimize performance, and prevent
          fraud.
        </li>
        <li>
          <strong>Usage and Gameplay Data</strong> Details about how you use our
          Apps. For example, game progress, levels completed, session duration,
          features used, in-game preferences, and crash/error reports. This
          information helps us fix bugs, analyze gameplay trends, and improve
          user experience.
        </li>
        <li>
          <strong>Payment Info</strong> If you make in-app purchases or
          subscribe to paid content, we receive information about the purchase.
          This can include purchase receipts, the product purchased, and
          transaction IDs from Apple’s App Store or Google Play. We do NOT
          collect or store your financial information (such as credit card
          numbers); payments are processed securely by the platform (Google or
          Apple) and we only receive confirmation of the transaction.
        </li>
        <li>
          <strong>Advertising Data</strong> We partner with advertising networks
          that display ads in our apps. These partners may collect data to
          personalize ads and measure effectiveness. This data includes your
          device identifiers (e.g. Google Advertising ID, IDFA), IP address,
          coarse location (e.g. country or city-level location inferred from IP
          or GPS if permission granted), and information about interactions with
          ads (such as the ads viewed or clicked).
        </li>
        <li>
          <strong>Analytics Data</strong> We use analytics services to
          understand how users engage with our Apps and to track the success of
          marketing campaigns. These services (like Firebase Analytics, Facebook
          Analytics tools, and AppsFlyer) automatically collect data such as app
          install source, how often you use the app, events within the app (e.g.
          level completion, button clicks), and device info. This helps us
          refine game design and marketing strategies.
        </li>
        <li>
          We may collect and process user-provided content submitted through
          AI-powered features, which may be transmitted to third-party AI
          providers as described in Section 9.
        </li>
      </ul>
      <h5>Third-Party Collection:</h5>
      <p>
        Please note that third-party services integrated in our apps (explained
        in the Third-Party Services section below) may collect data as well. For
        example, advertising and analytics providers gather device and usage
        data through their SDKs. We ensure that any third-party we work with is
        reputable and compliant with relevant privacy laws. However, their data
        handling is governed by their own privacy policies. We do not knowingly
        collect sensitive personal information such as health data, biometric
        data, or information about your race, religion, or political beliefs.
        Any personal data we collect is used only for the purposes described in
        this policy, such as improving our services, showing appropriate
        content/ads, and keeping our Apps running safely and fairly.
      </p>
      <h2>3. Children’s Privacy</h2>
      <p>
        Protecting children’s privacy is especially important to us. Some of our
        Apps are designed for or appealing to children under the age of 13, and
        we take special care to comply with laws like COPPA (Children’s Online
        Privacy Protection Act) and GDPR provisions for minors.
      </p>
      <ul>
        <li>
          <strong>Under 13:</strong> We do not knowingly collect personal
          information from children under 13 years of age without verifiable
          parental consent. Our children-focused Apps either do not request
          personal data or only collect what is necessary for the game’s
          operation with appropriate parental notice and consent. If we discover
          that a child under 13 has provided us with personal information
          without parental consent, we will take immediate steps to delete that
          information from our servers. For example, if a child sends us an
          email with their details, we will delete it and respond to inform them
          that we require parent/guardian contact.
        </li>
        <li>
          <strong>Parental Consent (Under 16)::</strong> In certain
          jurisdictions (such as the European Economic Area), the age threshold
          for required parental consent may be up to 16 years. If you are under
          16, please obtain permission from your parent or legal guardian before
          sharing any personal data or using any in-app features that collect
          personal information. We may employ age screens or obtain guardian
          consent in our apps to ensure compliance with applicable laws.
        </li>
        <li>
          <strong>Parents and Guardians:</strong> We encourage parents/guardians
          to monitor their children’s app usage and understand our privacy
          practices. If you are a parent or guardian and have concerns about
          your child’s personal information, you can contact us at our privacy
          email. You have the right to review, request deletion, or refuse
          further collection of your child’s information. We will honor such
          requests in accordance with applicable law.
        </li>
        <li>
          <strong>Child-Friendly Advertising:</strong> For Apps targeted to
          children, we take steps to disable interest-based (personalized)
          advertising and use only contextual or pre-approved ads in line with
          Google Play’s Families Policy and similar guidelines. This means the
          ads shown in kid-oriented apps are not based on tracking the child’s
          behavior. We also avoid inappropriate content in ads shown to children
          by using network filters.
        </li>
      </ul>
      <h2>4. Third-Party Services</h2>
      <p>
        Our apps and Apps incorporate third-party services for various purposes,
        including advertising, analytics, and social features. These third
        parties may collect and use certain information about you as described
        below:
      </p>
      <h3>Authentication Services</h3>
      <p>
        We allow users to sign in using third-party authentication providers.
      </p>
      <h4>Apple Sign In</h4>
      <p>If you choose to sign in using Apple, we may receive:</p>
      <ul>
        <li>Your name (if shared)</li>
        <li>Your email address (or private relay email)</li>
        <li>A unique Apple user identifier</li>
      </ul>
      <p>
        Apple processes authentication securely under its own privacy policy. We
        only use this data to create and manage your account within the app.
      </p>
      <p>
        <strong>Provider:</strong> Apple Inc. (United States)
        <br />
        https://www.apple.com/legal/privacy/
      </p>
      <h4>Google Sign In</h4>
      <p>If you choose Google Sign-In, we may receive:</p>
      <ul>
        <li>Your name</li>
        <li>Email address</li>
        <li>Google account unique identifier</li>
      </ul>
      <p>We use this solely for account authentication and management.</p>
      <p>
        <strong>Provider:</strong> Google LLC (United States)
        <br />
        https://policies.google.com/privacy
      </p>
      <h3>Subscription Management</h3>
      <p>
        We use:
        <br />
        RevenueCat, Inc. (United States)
        <br />
        https://www.revenuecat.com/privacy
      </p>
      <p>
        RevenueCat processes subscription transactions and purchase validation.
        Data shared may include:
      </p>
      <ul>
        <li>App User ID</li>
        <li>Purchase receipts</li>
        <li>Subscription status</li>
        <li>Device identifiers</li>
        <li>Transaction history</li>
      </ul>
      <p>
        RevenueCat does not receive your full payment details. Payments are
        processed securely by Apple App Store or Google Play.
      </p>
      <p>
        <strong>Purpose:</strong>
      </p>
      <ul>
        <li>Manage subscriptions</li>
        <li>Restore purchases</li>
        <li>Validate receipts</li>
        <li>Prevent fraud</li>
      </ul>

      <h3>Advertising Networks</h3>
      <p>
        We partner with the following advertising networks/SDKs to serve ads in
        our Apps. These partners help us monetize our free apps by showing you
        advertisements:
      </p>
      <ul>
        <li>
          <strong>Google AdMob:</strong> Google’s AdMob network provides in-app
          banner, interstitial, and rewarded video ads. AdMob may collect your
          Advertising ID (a unique, resettable identifier on your device), IP
          address, and information about your interactions with ads. This data
          is used to provide relevant ads and prevent fraud.
        </li>
        <li>
          <strong>AppLovin:</strong> AppLovin serves various ad formats in our
          Apps. It may collect device identifiers, usage data, and location info
          to optimize ad delivery and reporting.
        </li>
        <li>
          <strong>Unity Ads:</strong> Unity Ads (by Unity Technologies) shows
          video and playable ads. It can collect device information (like device
          model, OS, Advertising ID) and event data (e.g. whether you watched an
          ad) to tailor ads and track performance.
        </li>
        <li>
          <strong>Facebook Audience Network (Meta):</strong> Facebook’s Audience
          Network delivers ads in our apps, and may collect information such as
          your device identifiers and IP address to show targeted ads. If you
          have a Facebook account, Meta may also use data from your Facebook
          profile to personalize advertising in our app. (Note: We do not
          receive your Facebook profile data ourselves through ads, it’s used by
          the network internally.)
        </li>
        <li>
          <strong>Mintegral:</strong> Mintegral is a mobile advertising platform
          that may collect device and network information to serve ads,
          including region data and device identifiers.
        </li>
        <li>
          <strong>AdColony:</strong> AdColony provides video ads and might
          gather info like device IDs and general location to ensure ads are
          relevant and to track ad engagement (e.g., if you completed watching a
          rewarded video).
        </li>
        <li>
          <strong>Chartboost:</strong> Chartboost is an advertising network
          focused on mobile Apps. It may collect device identifiers, IP address,
          and in-game event data (such as level completion events) to target ads
          and measure their effectiveness.
        </li>
        <li>
          <strong>Vungle (Liftoff):</strong> Vungle (now part of Liftoff) offers
          rewarded video and other ad formats. It typically collects device
          information (Advertising ID, device model, etc.) and usage patterns to
          improve ad targeting and campaign analytics.
        </li>
      </ul>

      <p>
        When these ad networks are integrated in our apps, they may
        automatically collect the data mentioned (device IDs, IP, etc.) whenever
        an ad is requested or displayed. This data sharing allows ads to be
        tailored to you (for example, not showing the same ad repeatedly, or
        showing ads relevant to your interests) and helps us earn revenue to
        keep our Apps free. Each of these companies has its own privacy policy
        that explains their data practices in detail. We encourage you to review
        their policies (available on their websites) for more information. If
        required by your region’s laws, our apps will present you with a consent
        dialog for ad personalization (for instance, users in the European Union
        will be asked to consent to personalized ads in accordance with GDPR; if
        you decline consent, these networks will show non-personalized ads
        only).
      </p>

      <h3>Analytics and Other SDKs</h3>
      <p>
        We also use third-party analytics and tracking services to better
        understand our audience and improve our services:
      </p>
      <ul>
        <li>
          <strong>Firebase (Google):</strong> We use Google Firebase Analytics
          and Crashlytics in our apps. Firebase Analytics helps us see
          aggregated information like how often users open the app, which
          features are most popular, and what country our users are in.
          Crashlytics helps us identify and fix crashes or errors by sending
          crash reports from your device. Firebase may automatically collect
          device identifiers, usage data, IP addresses, and diagnostic
          information. Google’s privacy policy covers data usage for Firebase
          services.
        </li>
        <li>
          <strong>AppsFlyer:</strong> AppsFlyer is a mobile attribution and
          analytics platform. We use it to understand how users find our apps
          (e.g., which advertising campaigns or app store listings led you to
          install). AppsFlyer collects device and network identifiers, install
          and launch events, and may use this data to provide us aggregated
          reports on app performance. This helps us optimize our marketing
          efforts. AppsFlyer also has an Opt-Out option for users who do not
          want to be tracked (see the User Rights section below for opt-out
          details).
        </li>
        <li>
          <strong>Facebook SDK:</strong> In addition to ads, we integrate
          Facebook SDK for analytics and social features (like enabling you to
          share your progress or invite friends). The Facebook SDK may collect
          information such as your device info and app events (for example, when
          you install the app or complete a tutorial level) which can be linked
          to your Facebook account (if you have one) for analytics and
          advertising purposes. We might use aggregated insights from Facebook
          Analytics (if available) to improve our Apps, but we do not receive
          personally identifying info like your Facebook login details through
          this — any such sharing is under Facebook’s terms.
        </li>
      </ul>
      <h3>Attribution and Campaign Measurement</h3>
      <p>
        We use:
        <br />
        AppsFlyer Ltd. (Israel / United States)
        <br />
        https://www.appsflyer.com/legal/privacy-policy/
      </p>
      <p>AppsFlyer collects:</p>
      <ul>
        <li>Device identifiers (Advertising ID / IDFA)</li>
        <li>Install attribution data</li>
        <li>App events (e.g., purchases, retention events)</li>
        <li>IP address</li>
        <li>Campaign metadata</li>
      </ul>
      <p>
        <strong>Purpose:</strong>
      </p>
      <ul>
        <li>Measure marketing campaign effectiveness</li>
        <li>Detect fraud</li>
        <li>Optimize advertising spend</li>
      </ul>

      <p>
        These third-party services act as our service providers or partners, and
        they may process data about you for the purposes described. We only
        integrate services that are compliant with platform rules (Google Play,
        App Store) and privacy regulations. However, we do not control the data
        collected by third parties once it is transmitted to them. They use the
        data as per their privacy policies (for example, to build user profiles
        for advertising across various apps). We share data with them only to
        the extent necessary: for instance, we provide the ad networks with your
        Advertising ID so they can limit ad frequency and avoid fraud, and we
        send AppsFlyer certain in-app events for install attribution analysis.
      </p>

      <h3>Data Shared with Third Parties</h3>
      <p>
        Typically, the information shared with these third-party services
        includes: device identifiers, IP address, approximate location, in-app
        purchase events, and interactions within the app (like launching the app
        or completing a purchase or level). We do not share your direct contact
        info (like your name or email) with advertising networks. In some cases,
        you might independently choose to log in via a third-party (for example,
        connecting a game to Facebook or Google Play Apps for leaderboards or
        cloud save). In such cases, the third-party may receive data you allow
        based on their login/permission prompts (e.g., your profile name or
        friend list), but we only use that for the intended gameplay feature and
        abide by those third-party policies.
      </p>
      <p>
        <strong>5. User Rights and Data Retention</strong>
      </p>
      <p>
        <strong>Data Retention:</strong> We retain personal and user data only
        as long as necessary for the purposes described in this policy or as
        required by law or legitimate business purposes. This means:
      </p>
      <ul>
        <li>
          <strong>General Retention Period:</strong> We keep your data while you
          actively use our apps and for a reasonable period afterward. If you
          stop using the app, we may still retain some data for troubleshooting,
          legal, or accounting purposes. For example, we might keep crash logs
          or purchase records to address disputes or refunds.
        </li>
        <li>
          <strong>Third-Party Data Retention:</strong> Data that is collected by
          our third-party partners (advertisers, analytics) is stored according
          to their retention policies. We do not have direct control over how
          long, for instance, an ad network retains your Advertising ID or how
          long AppsFlyer keeps an attribution record. However, based on industry
          standards, such partners typically retain raw user-level data for a
          limited time (for example, advertising identifiers and analytic data
          are often kept for a minimum of 90 days up to a few years) to fulfill
          their services like fraud detection, campaign analysis, and required
          record-keeping. After that period, data may be aggregated or
          anonymized.
        </li>
        <li>
          <strong>Deletion of Data:</strong> When personal data is no longer
          needed, we will either securely delete it or anonymize it (so it can
          no longer be linked to you). For instance, if you request account
          deletion or we decide to discontinue a game, we will remove or
          de-identify your personal data associated with it, except any data we
          are required to keep by law (like tax records of purchases) or for
          legitimate interests (security logs to prevent fraud, for example)
          which we will also remove once no longer necessary. Backup copies of
          data might persist for a short time in secure archives, but we have
          processes to eventually purge those as well.
        </li>
      </ul>
      <p>
        <strong>Your Privacy Rights:</strong> We respect your rights over your
        personal information. Depending on your jurisdiction (e.g., the EU/EEA,
        California, etc.), you have certain rights that you can exercise. We
        provide all users worldwide with a baseline of privacy rights, and
        additional rights as required by applicable law:
      </p>
      <ul>
        <li>
          <strong>Right to Access:</strong> You can request a copy of the
          personal data we have collected about you. We will provide you with a
          summary of the information, how we&rsquo;ve used it, and who
          it&rsquo;s been shared with, in a portable format (to the extent
          required by law).
        </li>
        <li>
          <strong>Right to Rectification:</strong> If any personal information
          we have about you is incorrect or incomplete, you have the right to
          have it corrected. For example, if you contact us and realize we
          misspelled your name or recorded an incorrect email, we will update
          it.
        </li>
        <li>
          <strong>Right to Deletion:</strong> You may request that we delete the
          personal data we hold about you. This is also known as the
          &ldquo;Right to be Forgotten&rdquo; under GDPR. For instance, if you
          no longer want us to have your data, you can ask us to remove it from
          our records. We will delete the information from our systems and
          instruct third-party processors to do the same, barring any data we
          are required to retain (we&rsquo;ll inform you if that&rsquo;s the
          case). Do note that deleting essential data (like a unique identifier)
          may result in the loss of game progress or purchases, so we&rsquo;ll
          inform you of the consequences if you choose to delete data necessary
          for providing the service.
        </li>
        <li>
          <strong>Right to Object or Restrict Processing:</strong> You have the
          right to object to certain processing activities or request that we
          limit how we use your data. For example, you can object to processing
          for direct marketing or profiling. In practice, this means you can ask
          us to stop using your data for personalized advertising or analytics.
          We will comply unless we have compelling legitimate grounds to
          continue (in which case we&rsquo;ll explain those to you).
        </li>
        <li>
          <strong>Right to Withdraw Consent:</strong> If we are processing your
          data based on consent (for example, if you consented to personalized
          ads or to receiving a newsletter), you can withdraw that consent at
          any time. Withdrawing consent will not affect the lawfulness of any
          processing we did prior to your withdrawal. If you withdraw consent
          for something like targeted ads, we will stop that processing
          promptly.
        </li>
        <li>
          <strong>Right to Data Portability:</strong> For data you have provided
          to us directly (such as account information or user-generated
          content), you can request to receive it in a structured, commonly used
          electronic format, so you can transfer it to another service if you
          wish. (This typically applies if we have any account system; if our
          Apps do not have user accounts, this may be less relevant, but we
          honor it where applicable.)
        </li>
        <li>
          <strong>California Privacy Rights (CCPA/CPRA):</strong> If you are a
          California resident, you have the right to know the categories of
          personal information we collect, the sources of that information, the
          business or commercial purpose for collection, and the categories of
          third parties with whom we share personal information. Much of this is
          outlined in this policy. In summary, <strong>categories</strong> of
          data include identifiers (like device IDs, IP address), internet or
          electronic activity (app usage, interactions, cookies), approximate
          geolocation (region, country), and inferences drawn for ad
          personalization. <strong>Sources</strong> are you (the user) and
          automated collection through our app or third-party SDKs.{" "}
          <strong>Purposes</strong> include providing the service (game
          functionality), showing ads/marketing, analytics, and improving our
          offerings. <strong>Third parties</strong> we share with are the
          advertising and analytics partners listed in Section 4, and platform
          providers (Google, Apple) for things like purchase verification. Under
          CCPA, you also have the specific rights to:
        </li>
        <ul>
          <li>
            <strong>Opt-Out of Sale of Personal Information:</strong> We do{" "}
            <strong>not</strong> sell your personal information for money.
            However, the CCPA&rsquo;s definition of a "sale" can include sharing
            of personal identifiers with advertising partners to serve you
            personalized ads. If you prefer not to have your data shared for
            personalized advertising (i.e., to opt-out of any &ldquo;sale&rdquo;
            in the CCPA sense), you can request that we do so. We will then
            instruct our advertising partners to only serve you non-personalized
            ads and stop sharing your identifiers for ad targeting. To opt-out,
            you can use the in-app privacy settings (if available) or contact us
            at support@macromatrix.io with the subject &ldquo;CCPA
            Opt-Out&rdquo;.
          </li>
          <li>
            <strong>Right to Non-Discrimination:</strong> We will never deny you
            our services, charge you a different price, or provide a lesser
            experience because you exercised your privacy rights. If you opt out
            of personalized ads, for example, we will still provide the game to
            you&mdash;only the ads may be less relevant (but you will not be
            penalized in terms of access or features).
          </li>
          <li>
            <strong>
              Shine the Light (California Civil Code \u00a7 1798.83):
            </strong>{" "}
            Separate from CCPA, California&rsquo;s &ldquo;Shine the Light&rdquo;
            law allows users to request certain information about our disclosure
            of personal information to third parties for their direct marketing
            purposes. MacroMatrix Pvt Limited does <strong>not</strong> share
            personal information (like your email or name) with third parties
            for their own direct marketing uses without your consent.
          </li>
        </ul>
      </ul>
      <p>
        <strong>How to Exercise Your Rights:</strong> You can exercise any of
        the rights above by contacting us at support@macromatrix.io. Please
        specify your request clearly (for example, "I am requesting a copy of my
        data" or "Please delete my data associated with this device ID [include
        your Advertising ID]"). We may need to verify your identity to process
        certain requests &ndash; for instance, by asking you to provide
        information that matches our records (we do this to protect your data
        from unauthorized access). For deletion or access requests, if we do not
        have sufficient information to identify you (like if the only data we
        have is an anonymous device ID), we will correspond to gather necessary
        identifiers. We will respond to your request within the timeframe
        required by law: typically within 30 days for GDPR requests, and for
        CCPA requests, within 45 days (with an extension of another 45 days if
        needed, of which we would inform you). There is no fee for making these
        requests, though excessive or unfounded requests can be refused or may
        incur a fee as allowed by law (we will explain if that&rsquo;s the
        case).
      </p>
      <p>
        <strong>Opt-Out of Tracking and Personalized Ads:</strong> Aside from
        contacting us, you have several controls to limit data collection and
        personalized advertising:
      </p>
      <ul>
        <li>
          <strong>Device Settings (Advertising Preferences):</strong> Both
          Android and iOS devices provide a way to limit ad tracking. On
          Android, you can go to <strong>Settings &gt; Google &gt; Ads</strong>{" "}
          (or <strong>Privacy &gt; Ads</strong> on newer versions) and enable{" "}
          <em>&ldquo;Opt out of Ads Personalization.&rdquo;</em> On iOS, go to{" "}
          <strong>Settings &gt; Privacy &gt; Tracking</strong> and ensure apps
          must ask for permission to track; you can also toggle off{" "}
          <em>&ldquo;Allow Apps to Request to Track&rdquo;</em> to block all app
          tracking by default. If you&rsquo;ve already allowed tracking for our
          app and change your mind, you can disable the permission for our app
          in that Tracking menu. Using these settings will tell our advertising
          partners not to use your info for targeted ads. You may still see ads,
          but they will be more generic.
        </li>
        <li>
          <strong>In-App Opt-Out:</strong> If our game or a pop-up provides a
          &ldquo;Privacy Settings&rdquo; or &ldquo;Do Not Sell My Info&rdquo;
          option, you can use it to directly opt out of personalized advertising
          or data sharing. We will honor such preferences and signal to our ad
          partners to serve only non-targeted ads.
        </li>
        <li>
          <strong>AppsFlyer Opt-Out:</strong> If you wish to opt-out of
          AppsFlyer analytics specifically, AppsFlyer honors device-level
          opt-outs like the above settings. They also offer an independent
          opt-out: you can visit https://www.appsflyer.com/optout on your
          device&rsquo;s web browser, which will allow you to opt out your
          device from AppsFlyer analytics tracking.
        </li>
        <li>
          <strong>Interest-Based Ads Opt-Out Tools:</strong> Organizations in
          digital advertising offer tools like the{" "}
          <strong>DAA&rsquo;s AppChoices</strong> application. You can download
          the AppChoices app to manage ad preferences for many of the ad
          networks we use. Similarly,{" "}
          <strong>NAI&rsquo;s consumer opt-out</strong> page
          (networkadvertising.org) provides opt-out for member companies. Note
          that using these tools or device settings is usually specific to each
          device or browser, so if you use multiple devices, set your
          preferences on each.
        </li>
        <li>
          <strong>Push Notifications:</strong> Although not strictly
          &ldquo;tracking,&rdquo; if our Apps send push notifications and you
          prefer not to receive them, you can turn them off in your device
          settings (in iOS: Settings &gt; Notifications &gt; [Our Game]; in
          Android: Settings &gt; Apps &amp; notifications &gt; [Our Game] &gt;
          Notifications). This ensures we don&rsquo;t send you any promotional
          alerts.
        </li>
      </ul>
      <p>
        By providing these options, we aim to give you control over your data.
        Keep in mind that certain data (like necessary device info) might still
        be collected by our apps even if you opt out of personalized ads or
        analytics, but it would be used only for essential purposes (e.g.,
        serving contextual ads or ensuring the app works properly).
      </p>
      <p>
        <strong>6. Security Measures</strong>
      </p>
      <p>
        We take the security of your data seriously and implement a range of
        measures to protect it from unauthorized access, alteration, disclosure,
        or destruction. These measures include:
      </p>
      <ul>
        <li>
          <strong>Encryption:</strong> We use encryption to protect data in
          transit. Any data exchanged between your device and our servers (or
          third-party servers) is transmitted using HTTPS (SSL/TLS encryption).
          This prevents eavesdropping on your personal data during transmission.
          For example, when our app communicates with Firebase or our backend,
          the information is encrypted.
        </li>
        <li>
          <strong>Secure Storage:</strong> Personal data (to the extent we store
          any on our end) is kept on secure servers. We use reputable
          hosting/cloud providers that maintain robust security standards
          (firewalls, intrusion detection systems, etc.). We restrict access to
          these servers to authorized personnel only. If we store data like
          support emails or player feedback, those are in secure environments
          with access controls.
        </li>
        <li>
          <strong>Limited Access and Training:</strong> Within MacroMatrix Pvt
          Limited, your information is accessible only to employees and
          contractors who need it to perform their job (for example, customer
          support responding to an inquiry, or developers investigating a bug
          tied to a device log). We ensure our team is trained in
          confidentiality and privacy best practices. All team members must
          adhere to this Privacy Policy and are contractually obligated to
          protect your data.
        </li>
        <li>
          <strong>Third-Party Security:</strong> We choose third-party partners
          (analytics, advertising, etc.) that are committed to high security
          standards. For instance, companies like Google, Facebook, Unity, and
          AppLovin have industry-leading security practices. While we
          don&rsquo;t directly control their security, we rely on agreements
          that require them to protect data and to notify us of any breaches. We
          also periodically review their published security and privacy
          measures.
        </li>
        <li>
          <strong>Data Minimization:</strong> We collect only the data that we
          truly need for the purposes stated. By limiting the scope of data
          collected, we reduce the risk exposure. For example, if we just need
          an age bracket (adult or child) to comply with regulations, we might
          not ask for a full birthdate.
        </li>
        <li>
          <strong>Anonymization:</strong> Wherever feasible, we use anonymized
          or aggregated data rather than personal data. For instance, analytics
          reports we view are usually in aggregate form (total number of users
          in a country, device models percentages, etc.) without directly
          identifying individuals.
        </li>
        <li>
          <strong>Security Testing:</strong> We regularly update our
          applications and back-end systems to address security vulnerabilities.
          We use up-to-date libraries and SDKs, and follow platform security
          guidelines (Google Play and Apple). If we become aware of a security
          issue, we act swiftly to patch it. We also encourage users to update
          to the latest versions of our apps, which include the latest security
          fixes and improvements.
        </li>
      </ul>
      <p>
        Despite our best efforts, please note that{" "}
        <strong>
          no method of transmission over the internet or electronic storage is
          100% secure
        </strong>
        . Thus, we cannot guarantee absolute security of your information.
        However, we strive to protect your data to the best of our ability and
        maintain compliance with applicable data security regulations. In the
        unlikely event of a data breach that affects your personal information,
        we will notify you and the relevant authorities as required by law.
      </p>
      <p>
        <strong>7. Legal Compliance</strong>
      </p>
      <p>
        We comply with all relevant privacy laws and regulations in the regions
        where we operate. Here is how our practices align with major laws:
      </p>
      <ul>
        <li>
          <strong>GDPR (Europe):</strong> If you are a user in the European
          Union, United Kingdom, or other EEA countries, your personal data is
          processed in accordance with the GDPR. We act as the &ldquo;Data
          Controller&rdquo; for personal data you provide directly and data we
          collect via your use of our apps. We rely on certain legal bases for
          processing: (a) <strong>Consent</strong> &ndash; for example, we will
          ask for your consent to use your data for personalized advertising or
          to send you marketing communications; (b) <strong>Contract</strong>{" "}
          &ndash; when data processing is necessary to provide the service you
          requested (such as using your device data to deliver the game
          functionality, or processing an in-app purchase you initiated; by
          accepting our Terms of Service and using the app, you form a contract
          with us to play the game, which necessitates certain data use); (c){" "}
          <strong>Legitimate Interests</strong> &ndash; we may process limited
          data for our legitimate interests, such as improving our Apps or
          detecting fraud, but when we do so, we ensure it does not override
          your privacy rights (for instance, analyzing crash reports helps us
          improve stability for all users &ndash; this is a legitimate
          interest). We honor all the GDPR rights outlined in Section 5 above
          (access, deletion, etc.). Additionally, if we transfer personal data
          from the EU to our servers or third-parties in countries not deemed
          &ldquo;adequate&rdquo; by the EU (for example, to the United States),
          we ensure appropriate safeguards are in place. Typically, this means
          our contracts with service providers include{" "}
          <strong>Standard Contractual Clauses (SCCs)</strong> or the provider
          is certified under frameworks like EU-US data agreements (if
          applicable), ensuring EU-level data protection travels with your data.
          If you have any GDPR-specific questions or requests, you can contact
          us or our designated EU representative (if we appoint one; we will
          provide their contact upon request if required). You also have the
          right to lodge a complaint with your local Data Protection Authority
          if you believe we have infringed your privacy rights.
        </li>
        <li>
          <strong>CCPA (California):</strong> For California residents, in
          addition to the rights described in Section 5, we ensure that we
          provide clear notice of the personal information categories we collect
          and the purposes (done in this Privacy Policy and in the app&rsquo;s
          disclosures). We do not sell personal information for monetary gain.
          If in the future we ever consider selling data, we will update this
          policy and provide a proper opt-out mechanism. As noted, sharing data
          with third-party ad partners for advertising could be considered a
          &ldquo;sale&rdquo; under CCPA&rsquo;s broad definitions, so we treat
          it as such and honor opt-out requests. We have processes to respond to
          &ldquo;Do Not Sell&rdquo; requests within the required timelines. We
          also recognize the{" "}
          <strong>California Privacy Rights Act (CPRA)</strong> amendments,
          which introduce concepts like &ldquo;sensitive personal
          information&rdquo; and expanded rights&mdash;however, our apps
          generally do not collect sensitive personal info like precise
          geolocation, financial info, or identification numbers. If we ever do,
          we will give you the right to limit use of such sensitive data. Rest
          assured, we do not discriminate against users for exercising CCPA
          rights.
        </li>
        <li>
          <strong>
            COPPA (Children&rsquo;s Online Privacy Protection Act):
          </strong>{" "}
          As mentioned in the Children&rsquo;s Privacy section, for any users
          under 13 in the U.S., we either avoid collecting personal data or
          obtain verifiable parental consent via methods compliant with COPPA
          (such as an age gate or email consent process). We keep records of
          consents and allow parents to withdraw consent at any time.
        </li>
        <li>
          <strong>Google Play &amp; Apple App Store Requirements:</strong> We
          adhere to the Google Play Developer Policy and Apple App Store
          guidelines regarding user privacy. This means we accurately disclose
          our data practices in the Google Play &ldquo;Data Safety
          Section&rdquo; and Apple&rsquo;s &ldquo;App Privacy Details.&rdquo; We
          inform users about what data is collected and why, both in this policy
          and through any in-app prompts required. For example, Apple&rsquo;s
          App Tracking Transparency (ATT) framework is implemented: if our iOS
          apps engage in tracking for advertising, we will prompt you for
          permission to track across apps. If you decline, we will not access
          the IDFA or do cross-app tracking for advertising purposes. Similarly,
          we follow Google&rsquo;s policies on using the Advertising ID (which
          mandate that it&rsquo;s used only for advertising and analytics and
          not linked to personally identifiable information). Our inclusion in
          the &ldquo;Designed for Families&rdquo; program (if applicable for our
          kids&rsquo; apps) means we meet additional requirements like using
          only certified ad SDKs and ensuring content and ads are
          child-appropriate.
        </li>
        <li>
          <strong>Other Jurisdictions:</strong> If there are other privacy laws
          that apply (such as Brazil&rsquo;s LGPD, Canada&rsquo;s PIPEDA, etc.),
          we aim to meet those as well by providing transparency and control.
          This policy is intended to be comprehensive, but if any specific local
          law grants additional rights or imposes stricter standards, we will
          comply with those to the extent applicable.
        </li>
      </ul>
      <p>
        <strong>Legal Requests and Disclosures:</strong> We may disclose
        personal information if required to do so by law or in response to valid
        requests by public authorities (e.g., a court order, government demand,
        or subpoena). We will only disclose the minimum necessary data and,
        whenever permitted, will inform you of such disclosure. We may also
        disclose information if we believe in good faith that it is necessary
        to: investigate fraud, protect our users&rsquo; safety, enforce our
        Terms of Service, or respond to an emergency that poses a threat to
        health or safety.
      </p>
      <p>
        <strong>No Guarantees / Disclaimer:</strong> While this Privacy Policy
        is intended to outline comprehensive practices, it does not constitute a
        contract between you and us, and it may be updated as described below.
        We will treat your data in accordance with this policy and applicable
        law. In the event of any conflict between this policy and regional
        privacy laws, the applicable privacy laws will prevail to the extent of
        the conflict (we will still endeavor to meet the stricter requirement in
        each case).
      </p>
      <p>
        <strong>8. Updates and Contact Information</strong>
      </p>
      <p>
        <strong>Changes to This Privacy Policy:</strong> We may update or revise
        this Privacy Policy from time to time to reflect changes in our
        practices, technologies, legal requirements, or other factors. When we
        make changes, we will{" "}
        <strong>update the "Last Updated" date at the top</strong> of this
        policy. For significant changes, we will take additional steps to notify
        you: for example, we might display an in-app message or send a push
        notification or email (if we have your email on file through a
        newsletter or account) to inform you of the update. We encourage you to
        review this Privacy Policy periodically to stay informed about how we
        are protecting your information. Your continued use of our apps and Apps
        after any changes to the policy signifies your acceptance of the updated
        terms.
      </p>
      <p>
        <strong>9. AI-Powered Features and Data Processing</strong>
      </p>
      <p>
        Some features within our apps use artificial intelligence (AI) services
        provided by third-party partners to generate content or responses.
      </p>
      <p>
        When you use AI-powered features, we may transmit the following data to
        our AI processing partner:
      </p>
      <ul>
        <li>Text input you provide</li>
        <li>Images or media you upload (if applicable)</li>
        <li>Device language and region</li>
        <li>Technical request metadata necessary for processing</li>
      </ul>
      <p>
        We currently use:
        <br />
        OpenAI, L.L.C. (United States)
        <br />
        Privacy Policy: https://openai.com/privacy
      </p>
      <p>
        Data shared with OpenAI is used solely to generate AI responses
        requested by the user. We do not sell this data or use it for
        independent advertising purposes.
      </p>
      <p>
        We do not allow AI partners to use your data to identify you personally.
      </p>
      <p>
        <strong>User Consent</strong>
      </p>
      <p>
        Before any AI data is transmitted, the app requests your explicit
        consent. If you do not agree, AI features will remain disabled.
      </p>
      <p>
        <strong>Data Retention</strong>
      </p>
      <p>
        We do not permanently store AI prompts unless necessary for debugging,
        abuse prevention, or legal compliance. Any temporary logs are securely
        deleted after a limited retention period.
      </p>
      <p>
        <strong>International Transfers</strong>
      </p>
      <p>
        AI processing may occur outside your country of residence, including in
        the United States. Appropriate safeguards are implemented where required
        by law.
      </p>
      <p>
        <strong>Data Deletion</strong>
      </p>
      <p>
        You may request deletion of AI-related data by contacting
        support@macromatrix.io.
      </p>
      <p>
        <strong>Contact Us:</strong> If you have any questions, concerns, or
        requests regarding this Privacy Policy or our data practices, please do
        not hesitate to contact us:
      </p>
      <ul>
        <li>
          <strong>Email:</strong> You can reach our privacy team at
          support@macromatrix.io. For general inquiries, you can also contact
          contactmacromatrix.io. We strive to respond to all legitimate requests
          or questions within a reasonable timeframe.
        </li>
        <li>
          <strong>Mailing Address:</strong> Privacy Officer, MacroMatrix Pvt
          Limited, Office No 4-A-I Commercial Area, PGEHS, Lahore, Pakistan / 7th Floor, Opal Tower, 701–705, BJK Blvd, Business Bay, Dubai – UAE.{" "}
          <em>
            (Please include a return address and detailed message so we can
            respond appropriately.)
          </em>
        </li>
        <li>
          <strong>In-App Support:</strong> Some of our apps may have a support
          or feedback feature. You can use that to reach out to us with any
          privacy-related questions as well, and we will route your inquiry to
          the appropriate team.
        </li>
      </ul>
      <p>
        We value your privacy and trust. Our team is here to help and address
        any issues you might have regarding your personal data. If you feel that
        we have not addressed your concerns satisfactorily, you have the right
        to seek further recourse. For example, European users can contact their
        local Data Protection Authority, and California users can contact the
        California Attorney General&rsquo;s office. However, we sincerely invite
        you to first reach out to us so we can try to resolve the matter
        directly.
      </p>
      <p>
        Thank you for reading our Privacy Policy. By playing MacroMatrix Pvt
        Limited Apps, you&rsquo;ve placed trust in us to handle your information
        responsibly. We are continuously working to maintain that trust and
        deliver fun, safe experiences to all our players. If anything in this
        policy is unclear, please contact us and we&rsquo;ll be happy to explain
        or provide further information. Your privacy matters to us!
      </p>
    </div>
  );
}

export default PrivacyPolicy;
